import { Box, Button, CircularProgress, MenuItem, Stack, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { API_BASE_URL, authFetch, authFetchJson } from '../../apiClient.ts';
import { downloadBlob } from '../../utils/helperUtils.ts';

interface ContactReportRow {
  id: number;
  firstName: string;
  lastName: string;
  primaryEmail: string;
  primaryPhone: string;
  company: string;
  status: string;
}

interface ContactStatus {
  id: number;
  name: string;
}

const columns: GridColDef[] = [
  { field: 'firstName', headerName: 'First Name', width: 160 },
  { field: 'lastName', headerName: 'Last Name', width: 160 },
  { field: 'status', headerName: 'Status', width: 130 },
  { field: 'company', headerName: 'Company', width: 200 },
  { field: 'primaryEmail', headerName: 'Email', flex: 1, minWidth: 200 },
  { field: 'primaryPhone', headerName: 'Phone', width: 160 }
];

export const ContactsReport: React.FC = () => {
  const [rows, setRows] = useState<ContactReportRow[]>([]);
  const [statuses, setStatuses] = useState<ContactStatus[]>([]);
  const [statusId, setStatusId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    authFetchJson(`${API_BASE_URL}/contact-statuses`)
      .then(({ json }) => setStatuses(json))
      .catch((error) => console.error('Failed to load contact statuses', error));
  }, []);

  useEffect(() => {
    // Unlike the other reports this one refetches, so a slow earlier response
    // must not overwrite a newer one.
    let current = true;
    setLoading(true);
    const query = statusId ? `?contactStatusId=${statusId}` : '';
    authFetchJson(`${API_BASE_URL}/reports/contacts${query}`)
      .then(({ json }) => {
        if (!current) return;
        setRows(json);
        setLoading(false);
      })
      .catch((error) => {
        if (!current) return;
        console.error('Failed to load contacts report', error);
        setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [statusId]);

  const downloadCsv = async () => {
    const query = statusId ? `?contactStatusId=${statusId}` : '';
    try {
      const response = await authFetch(`${API_BASE_URL}/reports/contacts/csv${query}`, {
        method: 'GET'
      });
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      downloadBlob(
        await response.blob(),
        `contacts-report-${new Date().toISOString().slice(0, 10)}.csv`
      );
    } catch (error) {
      console.error('Failed to download contacts report', error);
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <TextField
          select
          label="Status"
          size="small"
          sx={{ minWidth: 200 }}
          value={statusId}
          onChange={(e) => setStatusId(e.target.value)}
          helperText={statuses.length === 0 ? 'No contact statuses set up yet' : undefined}
        >
          <MenuItem value="">All</MenuItem>
          {statuses.map((status) => (
            <MenuItem key={status.id} value={String(status.id)}>
              {status.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={downloadCsv}>
          Download CSV
        </Button>
      </Stack>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pagination
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            density="standard"
            disableRowSelectionOnClick
          />
        </Box>
      )}
    </Box>
  );
};
